import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { 
  Camera, ChevronLeft, ChevronRight, Search, Calendar, 
  X, ZoomIn, Download, User, Clock, FolderOpen, Maximize2, Loader2, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, subDays, isToday } from 'date-fns';

const TeamScreenshots = () => {
  const { api, token, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [projects, setProjects] = useState({});
  const [imageUrls, setImageUrls] = useState({});
  const [loadingImages, setLoadingImages] = useState({});
  const imageUrlsRef = useRef({});

  // Fetch authenticated image and return blob URL
  const fetchImageUrl = useCallback(async (screenshotId, type = 'thumbnail') => {
    const cacheKey = `${screenshotId}_${type}`;
    if (imageUrlsRef.current[cacheKey]) return imageUrlsRef.current[cacheKey];

    setLoadingImages(prev => ({ ...prev, [cacheKey]: true }));
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/screenshots/${screenshotId}/${type}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      imageUrlsRef.current[cacheKey] = url;
      setImageUrls(prev => ({ ...prev, [cacheKey]: url }));
      return url;
    } catch (error) {
      console.error('Failed to fetch image:', error);
      return null;
    } finally {
      setLoadingImages(prev => ({ ...prev, [cacheKey]: false }));
    }
  }, [token]);

  // Preload thumbnails when screenshots change
  useEffect(() => {
    screenshots.forEach(shot => {
      fetchImageUrl(shot.id, 'thumbnail');
    });
    return () => {
      // Cleanup blob URLs on unmount
      Object.values(imageUrlsRef.current).forEach(url => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [screenshots, fetchImageUrl]);

  // Preload full image when lightbox opens
  useEffect(() => {
    if (lightboxOpen && screenshots[lightboxIndex]) {
      fetchImageUrl(screenshots[lightboxIndex].id, 'image');
    }
  }, [lightboxOpen, lightboxIndex, screenshots, fetchImageUrl]);

  const fetchData = useCallback(async (userId = null) => {
    if (!api) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ date: selectedDate });
      if (userId) params.append('user_id', userId);

      const res = await api.get(`/screenshots/team-overview?${params}`);
      setMembers(res.data.members || []);
      setScreenshots(res.data.screenshots || []);
    } catch (err) {
      toast.error('Failed to load team screenshots');
    } finally {
      setLoading(false);
    }
  }, [api, selectedDate]);

  // Fetch projects for enriching screenshot data
  useEffect(() => {
    if (!api) return;
    api.get('/projects').then(res => {
      const map = {};
      (res.data || []).forEach(p => { map[p.id] = p.name; });
      setProjects(map);
    }).catch(() => {});
  }, [api]);

  // Initial load — fetch members for date
  useEffect(() => {
    fetchData(selectedUser?.id || null);
  }, [fetchData, selectedUser]);

  const handleSelectUser = (member) => {
    setSelectedUser(member);
    setLightboxOpen(false);
  };

  const handleDeleteScreenshot = async (screenshotId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this screenshot permanently?')) return;
    try {
      await api.delete(`/screenshots/${screenshotId}`);
      setScreenshots(prev => prev.filter(s => s.id !== screenshotId));
      // Revoke blob URLs
      const thumbKey = `${screenshotId}_thumbnail`;
      const imgKey = `${screenshotId}_image`;
      if (imageUrlsRef.current[thumbKey]) URL.revokeObjectURL(imageUrlsRef.current[thumbKey]);
      if (imageUrlsRef.current[imgKey]) URL.revokeObjectURL(imageUrlsRef.current[imgKey]);
      delete imageUrlsRef.current[thumbKey];
      delete imageUrlsRef.current[imgKey];
      toast.success('Screenshot deleted');
    } catch {
      toast.error('Failed to delete screenshot');
    }
  };

  const handleDateChange = (direction) => {
    const current = new Date(selectedDate + 'T12:00:00');
    const next = direction === 'next' ? addDays(current, 1) : subDays(current, 1);
    if (next > new Date()) return;
    setSelectedDate(next.toISOString().split('T')[0]);
  };

  const handleOpenLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const handleLightboxNav = (direction) => {
    if (direction === 'next' && lightboxIndex < screenshots.length - 1) {
      setLightboxIndex(prev => prev + 1);
    } else if (direction === 'prev' && lightboxIndex > 0) {
      setLightboxIndex(prev => prev - 1);
    }
  };

  const filteredMembers = members.filter(m =>
    m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalScreenshots = members.reduce((sum, m) => sum + m.screenshot_count, 0);
  const activeMembers = members.filter(m => m.screenshot_count > 0).length;
  const dateLabel = isToday(new Date(selectedDate + 'T12:00:00'))
    ? 'Today'
    : format(new Date(selectedDate + 'T12:00:00'), 'EEE, MMM d');

  return (
    <Layout>
      <div className="min-h-[calc(100vh-4rem)]" data-testid="team-screenshots-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              Team Screenshots
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {activeMembers} active member{activeMembers !== 1 ? 's' : ''} &middot; {totalScreenshots} screenshot{totalScreenshots !== 1 ? 's' : ''} {dateLabel === 'Today' ? 'today' : `on ${dateLabel}`}
            </p>
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-2" data-testid="date-nav">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => handleDateChange('prev')}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg min-w-[150px] justify-center">
              <Calendar className="w-4 h-4 text-slate-400" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="bg-transparent text-sm font-medium text-slate-700 dark:text-slate-200 border-none outline-none cursor-pointer"
                data-testid="date-picker"
              />
            </div>
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => handleDateChange('next')}
              disabled={isToday(new Date(selectedDate + 'T12:00:00'))}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Left Panel — Employee List */}
          <div className="col-span-4 xl:col-span-3">
            <Card className="sticky top-4">
              <CardContent className="p-0">
                {/* Search */}
                <div className="p-3 border-b border-slate-100 dark:border-slate-800">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search employees..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9 text-sm"
                      data-testid="search-employees"
                    />
                  </div>
                </div>

                {/* Member List */}
                <div className="max-h-[calc(100vh-16rem)] overflow-y-auto">
                  {filteredMembers.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-400">
                      No team members found
                    </div>
                  ) : (
                    filteredMembers.map((member) => (
                      <button
                        key={member.id}
                        onClick={() => handleSelectUser(member)}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 border-b border-slate-50 dark:border-slate-800/50 ${
                          selectedUser?.id === member.id
                            ? 'bg-indigo-50 dark:bg-indigo-900/20 border-l-3 border-l-indigo-500'
                            : ''
                        }`}
                        data-testid={`member-${member.id}`}
                      >
                        {/* Avatar */}
                        <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold ${
                          selectedUser?.id === member.id
                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-800/40 dark:text-indigo-300'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        }`}>
                          {member.profile_photo ? (
                            <img src={member.profile_photo} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            member.name?.charAt(0)?.toUpperCase() || <User className="w-4 h-4" />
                          )}
                        </div>

                        {/* Name + Email */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            selectedUser?.id === member.id ? 'text-indigo-700 dark:text-indigo-300' : 'text-slate-800 dark:text-slate-200'
                          }`}>
                            {member.name}
                          </p>
                          <p className="text-xs text-slate-400 truncate">{member.email}</p>
                        </div>

                        {/* Screenshot Count Badge */}
                        <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                          member.screenshot_count > 0
                            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                            : 'bg-slate-100 text-slate-400 dark:bg-slate-700 dark:text-slate-500'
                        }`}>
                          {member.screenshot_count}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Panel — Screenshot Grid */}
          <div className="col-span-8 xl:col-span-9">
            {!selectedUser ? (
              <div className="flex flex-col items-center justify-center h-[60vh] text-center">
                <div className="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
                  <Camera className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300">Select an employee</h3>
                <p className="text-sm text-slate-400 mt-1 max-w-sm">
                  Choose a team member from the list to view their captured screenshots for {dateLabel.toLowerCase()}
                </p>
              </div>
            ) : loading ? (
              <div className="flex items-center justify-center h-[60vh]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <>
                {/* Selected User Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-800/40 flex items-center justify-center text-sm font-semibold text-indigo-700 dark:text-indigo-300">
                      {selectedUser.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{selectedUser.name}</h2>
                      <p className="text-xs text-slate-400">{selectedUser.email} &middot; {screenshots.length} screenshot{screenshots.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedUser(null)}
                    className="text-slate-400 hover:text-slate-600"
                    data-testid="clear-selection"
                  >
                    <X className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>

                {/* Screenshot Grid */}
                {screenshots.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[50vh] text-center">
                    <Camera className="w-12 h-12 text-slate-300 dark:text-slate-600 mb-3" />
                    <h3 className="text-base font-medium text-slate-600 dark:text-slate-400">No screenshots</h3>
                    <p className="text-sm text-slate-400 mt-1">
                      {selectedUser.name} has no captured screenshots for {dateLabel.toLowerCase()}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-4" data-testid="screenshot-grid">
                    {screenshots.map((shot, index) => (
                      <Card 
                        key={shot.id} 
                        className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                        onClick={() => handleOpenLightbox(index)}
                        data-testid={`screenshot-${shot.id}`}
                      >
                        {/* Thumbnail */}
                        <div className="relative aspect-video bg-slate-100 dark:bg-slate-800">
                          {loadingImages[`${shot.id}_thumbnail`] ? (
                            <div className="w-full h-full flex items-center justify-center">
                              <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                            </div>
                          ) : imageUrls[`${shot.id}_thumbnail`] ? (
                            <img
                              src={imageUrls[`${shot.id}_thumbnail`]}
                              alt={`Screenshot at ${format(new Date(shot.captured_at), 'h:mm a')}`}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Camera className="w-6 h-6 text-slate-300" />
                            </div>
                          )}
                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <Maximize2 className="w-6 h-6 text-white drop-shadow-lg" />
                            </div>
                          </div>
                        </div>

                        {/* Metadata */}
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                              <Clock className="w-3 h-3" />
                              {format(new Date(shot.captured_at), 'h:mm:ss a')}
                            </div>
                            <div className="flex items-center gap-2">
                              {shot.project_id && projects[shot.project_id] && (
                                <div className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400 truncate max-w-[50%]">
                                  <FolderOpen className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{projects[shot.project_id]}</span>
                                </div>
                              )}
                              {user?.role === 'admin' && (
                                <button
                                  onClick={(e) => handleDeleteScreenshot(shot.id, e)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-500"
                                  title="Delete screenshot"
                                  data-testid={`delete-screenshot-${shot.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </div>
                          {shot.description && (
                            <p className="text-xs text-slate-400 mt-1.5 truncate">{shot.description}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Lightbox */}
        <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 bg-black/95 border-none overflow-hidden" data-testid="screenshot-lightbox">
            {screenshots[lightboxIndex] && (
              <>
                {/* Close */}
                <button
                  onClick={() => setLightboxOpen(false)}
                  className="absolute top-4 right-4 z-50 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                  data-testid="lightbox-close"
                >
                  <X className="w-5 h-5 text-white" />
                </button>

                {/* Image */}
                <div className="flex items-center justify-center w-full h-[80vh]">
                  {loadingImages[`${screenshots[lightboxIndex].id}_image`] ? (
                    <Loader2 className="w-10 h-10 text-white/60 animate-spin" />
                  ) : imageUrls[`${screenshots[lightboxIndex].id}_image`] ? (
                    <img
                      src={imageUrls[`${screenshots[lightboxIndex].id}_image`]}
                      alt="Full screenshot"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <Camera className="w-12 h-12 text-white/40" />
                  )}
                </div>

                {/* Navigation Arrows */}
                {lightboxIndex > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLightboxNav('prev'); }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    data-testid="lightbox-prev"
                  >
                    <ChevronLeft className="w-5 h-5 text-white" />
                  </button>
                )}
                {lightboxIndex < screenshots.length - 1 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLightboxNav('next'); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    data-testid="lightbox-next"
                  >
                    <ChevronRight className="w-5 h-5 text-white" />
                  </button>
                )}

                {/* Bottom Info Bar */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 pt-12">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-white/80 text-sm">
                      <span className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        {format(new Date(screenshots[lightboxIndex].captured_at), 'h:mm:ss a')}
                      </span>
                      {screenshots[lightboxIndex].project_id && projects[screenshots[lightboxIndex].project_id] && (
                        <span className="flex items-center gap-1.5">
                          <FolderOpen className="w-4 h-4" />
                          {projects[screenshots[lightboxIndex].project_id]}
                        </span>
                      )}
                      {screenshots[lightboxIndex].description && (
                        <span className="text-white/60">{screenshots[lightboxIndex].description}</span>
                      )}
                    </div>
                    <span className="text-white/50 text-sm">
                      {lightboxIndex + 1} / {screenshots.length}
                    </span>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TeamScreenshots;
